"use client";

import React from "react";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage, FormDescription } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { Loader, Mail, Clock, Shield } from "lucide-react";
function DirectInquiryForm() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div data-section-id="116882" className="bg-[#141A22] border border-primary/20 p-8 rounded-none flex flex-col items-center justify-center text-center min-h-[400px]">
                <Shield className="size-12 text-primary mb-6" />
                <h3 className="font-sans text-2xl text-foreground mb-4 uppercase tracking-widest">Inquiry Received</h3>
                <p className="font-serif text-lg text-foreground/80 mb-8 max-w-md italic">
                    {successMessage}
                </p>
                <Button onClick={resetForm} variant="outline" className="rounded-none border-primary/50 text-primary hover:bg-primary hover:text-primary-foreground font-sans tracking-widest uppercase">
                    Submit New Inquiry
                </Button>
            </div>;
  }
  return <Form onSubmit={handleSubmit} className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <FormField control={control} name="name" rules={{
        required: "Name is required"
      }} render={({
        field
      }) => <FormItem>
                            <FormLabel className="font-sans text-xs tracking-widest uppercase text-foreground/70">Full Name</FormLabel>
                            <FormControl>
                                <Input placeholder="Enter your name" className="bg-[#0A0E14] border-primary/20 rounded-none font-serif text-lg focus-visible:border-primary focus-visible:ring-0 h-12 placeholder:text-foreground/30" {...field} />
                            </FormControl>
                            <FormDescription className="font-serif text-xs text-foreground/50 italic">
                                Please provide the name associated with your account or inquiry.
                            </FormDescription>
                            <FormMessage className="font-serif text-sm" />
                        </FormItem>} />

                <FormField control={control} name="email" rules={{
        required: "Email is required",
        pattern: {
          value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
          message: "Please enter a valid email address"
        }
      }} render={({
        field
      }) => <FormItem>
                            <FormLabel className="font-sans text-xs tracking-widest uppercase text-foreground/70">Email Address</FormLabel>
                            <FormControl>
                                <Input type="email" placeholder="name@domain.com" className="bg-[#0A0E14] border-primary/20 rounded-none font-serif text-lg focus-visible:border-primary focus-visible:ring-0 h-12 placeholder:text-foreground/30" {...field} />
                            </FormControl>
                            <FormDescription className="font-serif text-xs text-foreground/50 italic">
                                Our systematic responses are delivered to this address.
                            </FormDescription>
                            <FormMessage className="font-serif text-sm" />
                        </FormItem>} />
            </div>

            <FormField control={control} name="subject" render={({
      field
    }) => <FormItem>
                        <FormLabel className="font-sans text-xs tracking-widest uppercase text-foreground/70">Subject Line</FormLabel>
                        <FormControl>
                            <Input type="text" placeholder="e.g., The Planner Access" className="bg-[#0A0E14] border-primary/20 rounded-none font-serif text-lg focus-visible:border-primary focus-visible:ring-0 h-12 placeholder:text-foreground/30" {...field} />
                        </FormControl>
                        <FormMessage className="font-serif text-sm" />
                    </FormItem>} />

            <FormField control={control} name="message" rules={{
      required: "Message is required"
    }} render={({
      field
    }) => <FormItem>
                        <FormLabel className="font-sans text-xs tracking-widest uppercase text-foreground/70">Inquiry Details</FormLabel>
                        <FormControl>
                            <Textarea placeholder="State your objective or question clearly..." className="bg-[#0A0E14] border-primary/20 rounded-none font-serif text-lg focus-visible:border-primary focus-visible:ring-0 min-h-[160px] resize-y placeholder:text-foreground/30" {...field} />
                        </FormControl>
                        <FormDescription className="font-serif text-xs text-foreground/50 italic">
                            Detailing your specific needs allows for a more precise response.
                        </FormDescription>
                        <FormMessage className="font-serif text-sm" />
                    </FormItem>} />

            {submitError && <div className="p-4 bg-destructive/10 border border-destructive/30 rounded-none">
                    <p className="text-destructive font-serif text-sm italic">{submitError}</p>
                </div>}

            <Button type="submit" disabled={isSubmitting} className="w-full md:w-auto rounded-none bg-primary text-primary-foreground hover:bg-[#D4AE52] font-sans tracking-widest uppercase h-12 px-12 transition-colors duration-300">
                {isSubmitting ? <>
                        <Loader className="animate-spin mr-2 size-4" />
                        Processing...
                    </> : 'Send Message'}
            </Button>
        </Form>;
}
export default function DirectInquiry() {
  const {
    ref: leftColRef,
    isVisible: leftColVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  const {
    ref: rightColRef,
    isVisible: rightColVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  return <section id="direct-inquiry" className="dark bg-[#0A0E14] text-[#F4EFE6] py-24 px-4 sm:px-6 lg:px-8 overflow-hidden">
            <div className="max-w-7xl mx-auto">
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16">
                    
                    {}
                    <div ref={leftColRef} className={`lg:col-span-7 transition-all duration-1000 ease-out ${leftColVisible ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-8'}`}>
                        <div className="mb-10">
                            <h2 className="font-sans text-3xl md:text-4xl text-[#F4EFE6] uppercase tracking-widest mb-4">
                                Direct <span className="text-primary">Inquiry</span>
                            </h2>
                            <div className="h-px w-24 bg-primary/50 mb-6"></div>
                            <p className="font-serif text-lg text-[#F4EFE6]/70 max-w-xl italic">
                                Connect with our team for specific questions regarding our systematic trading frameworks, educational resources, or institutional partnership opportunities.
                            </p>
                        </div>

                        <FormProvider formId="direct_inquiry_connect" sectionName="Direct Inquiry" defaultValues={{
            name: "",
            email: "",
            subject: "",
            message: ""
          }} validationRules={{
            name: {
              required: true
            },
            email: {
              required: true,
              pattern: "email"
            },
            message: {
              required: true
            }
          }} submitText="Send Message" successMessage="Your inquiry has been logged within our system. Our team will review your request and respond with precision.">
                            <DirectInquiryForm />
                        </FormProvider>
                    </div>

                    {}
                    <div ref={rightColRef} className={`lg:col-span-5 flex flex-col gap-6 transition-all duration-1000 delay-200 ease-out ${rightColVisible ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-8'}`}>
                        {}
                        <div className="bg-[#141A22] p-8 border border-primary/10 rounded-none group hover:border-primary/40 transition-colors duration-500">
                            <div className="flex items-center gap-4 mb-4">
                                <div className="p-3 bg-[#0A0E14] border border-primary/20 rounded-none">
                                    <Mail className="size-5 text-primary" />
                                </div>
                                <h3 className="font-sans text-lg text-[#F4EFE6] uppercase tracking-widest">Support Desk</h3>
                            </div>
                            <p className="font-serif text-[#F4EFE6]/70 mb-6 leading-relaxed italic">
                                For immediate assistance or specific inquiries regarding your account access or our educational materials, reach out directly to our support team.
                            </p>
                            <Link to="mailto:support@macaronicapital.io" className="inline-flex items-center font-sans text-sm tracking-widest uppercase text-primary hover:text-[#D4AE52] transition-colors">
                                support@macaronicapital.io
                            </Link>
                        </div>

                        {}
                        <div className="bg-[#141A22] p-8 border border-primary/10 rounded-none group hover:border-primary/40 transition-colors duration-500">
                            <div className="flex items-center gap-4 mb-4">
                                <div className="p-3 bg-[#0A0E14] border border-primary/20 rounded-none">
                                    <Clock className="size-5 text-primary" />
                                </div>
                                <h3 className="font-sans text-lg text-[#F4EFE6] uppercase tracking-widest">Response Protocol</h3>
                            </div>
                            <p className="font-serif text-[#F4EFE6]/70 leading-relaxed italic">
                                We operate with the same systematic precision as our trading frameworks. All inquiries are processed and responded to within 24-48 business hours, ensuring defined and reliable communication.
                            </p>
                        </div>

                        {}
                        <div className="bg-[#141A22] p-6 border-l-2 border-primary rounded-none mt-auto">
                            <p className="font-serif text-[15px] leading-relaxed text-[#F4EFE6]/80 italic">
                                "I’m standing by to route your transmission. In a world of noise, we prioritize the signal. Looking forward to simplifying your system." — The Caddie
                            </p>
                        </div>
                    </div>

                </div>
            </div>
        </section>;
}